/**
 * DEAD SIGNAL — Evidence Board Overlay (the persistent evidence locker)
 *
 * Full-screen overlay showing collected evidence as pixel-art styled cards.
 * Toggled via terminal command or keyboard shortcut. Reads canonical D5
 * records through the evidenceCanon accessor (interim mirror of the
 * Knowledge lane's knowledge/index.js API).
 *
 * Each card renders its evidence image with click-to-zoom inspection; an
 * absent PNG renders a labeled placeholder, never a broken image element.
 * Styles for the locker-specific elements are scoped here (ds-ev-*) so no
 * shared stylesheet is touched.
 */
import React, { useEffect, useState } from 'react';
import { useGameStore } from '../state/gameState.js';
import { getEvidence } from '../content/evidenceCanon.js';

/**
 * Resolve an evidence record to its forensic-scan PNG. Filenames are the
 * lowercased canonical id under /images/evidence/, so the path tracks the
 * ratified id and matches IMAGE_MANIFEST.md without per-record wiring.
 */
function evidenceImageSrc(item) {
  if (!item || !item.id) return null;
  // Forensic-scan plates ship as .webp under /images/evidence/, named by the
  // lowercased canonical id (matches IMAGE_MANIFEST.md).
  return `/images/evidence/${String(item.id).toLowerCase()}.webp`;
}

/**
 * Evidence visual with the graceful-degradation contract: a missing record or
 * a PNG that has not been dropped in yet renders a labeled placeholder frame,
 * so the locker never shows a broken image. The image arrives later as a PNG
 * named for the canonical id; until then every card shows the placeholder.
 * Exported for EvidenceDossier.
 */
export function EvidenceImage({ item, className = '', onClick }) {
  const [failed, setFailed] = useState(false);
  const src = evidenceImageSrc(item);
  useEffect(() => { setFailed(false); }, [src]);

  if (!item) return null;
  const showPlaceholder = !src || failed;

  if (showPlaceholder) {
    return (
      <div
        className={`ds-ev-img ds-ev-img-placeholder ${className}`}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        aria-label={`${item.title}: no visual on file`}
      >
        <span className="ds-ev-ph-glyph">◈</span>
        <span className="ds-ev-ph-label">{item.title}</span>
        <span className="ds-ev-ph-sub">VISUAL RECORD PENDING DIGITIZATION</span>
      </div>
    );
  }

  return (
    <img
      className={`ds-ev-img ${className}`}
      src={src}
      alt={item.title}
      onError={() => setFailed(true)}
      onClick={onClick}
    />
  );
}

/** Full-screen zoom inspection of one evidence visual. ESC or click closes. */
export function EvidenceZoom({ item, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!item) return null;
  return (
    <div className="ds-ev-zoom" onClick={onClose} role="dialog" aria-label={`Inspecting: ${item.title}`}>
      <div className="ds-ev-zoom-frame" onClick={(e) => e.stopPropagation()}>
        <div className="ds-ev-zoom-bar">
          <span>◆ INSPECTING — {item.title}</span>
          <button type="button" className="ds-ev-zoom-close" onClick={onClose}>[ ESC ]</button>
        </div>
        <EvidenceImage item={item} className="ds-ev-zoom-img" />
        <div className="ds-ev-zoom-caption">{item.description}</div>
      </div>
    </div>
  );
}

// De-slug a scene id into a readable origin label.
function humanizeScene(id) {
  return String(id || '—').replace(/^ds_/, '').replace(/_/g, ' ');
}

/** Apply visual corruption to text */
function corruptText(text) {
  const glitchChars = '█▓▒░╬╫╪┼┤├╡╞╟╢';
  return text
    .split('')
    .map((ch) => (Math.random() > 0.85 ? glitchChars[Math.floor(Math.random() * glitchChars.length)] : ch))
    .join('');
}

const EV_STYLES = `
.ds-ev-img { display: block; width: 100%; cursor: zoom-in; image-rendering: pixelated; }
.ds-ev-card-img { aspect-ratio: 4 / 3; object-fit: cover; margin-bottom: 8px; border: 1px solid rgba(0,255,204,0.25); background: #04101f; }
.ds-ev-img-placeholder { aspect-ratio: 4 / 3; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; border: 1px dashed rgba(0,255,204,0.35); background: repeating-linear-gradient(45deg, #04101f, #04101f 6px, #071a2e 6px, #071a2e 12px); color: #7fa8c4; text-align: center; padding: 8px; }
.ds-ev-ph-glyph { font-size: 22px; color: #00ffcc; opacity: 0.7; }
.ds-ev-ph-label { font-size: 11px; letter-spacing: 1px; text-transform: uppercase; }
.ds-ev-ph-sub { font-size: 9px; opacity: 0.55; letter-spacing: 1px; }
.ds-ev-zoom { position: fixed; inset: 0; z-index: 1200; background: rgba(0, 8, 20, 0.92); display: flex; align-items: center; justify-content: center; cursor: zoom-out; }
.ds-ev-zoom-frame { max-width: min(880px, 92vw); max-height: 92vh; overflow: auto; background: #020a14; border: 1px solid rgba(0,255,204,0.4); box-shadow: 0 0 32px rgba(0,255,204,0.15); cursor: default; }
.ds-ev-zoom-bar { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 8px 12px; color: #00ffcc; font-size: 12px; letter-spacing: 1px; border-bottom: 1px solid rgba(0,255,204,0.25); }
.ds-ev-zoom-close { background: none; border: 1px solid rgba(0,255,204,0.5); color: #00ffcc; cursor: pointer; font: inherit; font-size: 13px; padding: 4px 12px; letter-spacing: 1px; transition: border-color 80ms, background 80ms; }
.ds-ev-zoom-close:hover { background: rgba(0,255,204,0.1); border-color: rgba(0,255,204,0.75); }
.ds-ev-zoom-img { cursor: default; }
.ds-ev-zoom-caption { padding: 10px 12px; color: #9fb4c8; font-size: 12px; line-height: 1.5; }
.ds-ev-present { margin-top: 8px; width: 100%; background: none; border: 1px solid rgba(255,170,0,0.5); color: #ffaa00; font: inherit; font-size: 11px; letter-spacing: 1px; padding: 4px 8px; cursor: pointer; }
.ds-ev-present:hover { background: rgba(255,170,0,0.12); }
.ds-ev-present[disabled] { opacity: 0.45; cursor: default; }
`;

/** Scoped ds-ev-* styles; rendered by the board and the dossier so either
 * surface carries the rules regardless of which is mounted. */
export function EvidenceStyles() {
  return <style>{EV_STYLES}</style>;
}

/**
 * EVIDENCE LOCKER — in-frame `.glass-wide` panel (absolute inset:0, bounded by
 * `.hud-mid`, blocking). Replaces the old full-screen `.evidence-board`. Grid of
 * logged records; clicking one opens an in-panel detail view with description,
 * classification, cross-refs, present-to-Hargrove (during an interview), and
 * click-to-zoom. Driven by the real store (isEvidenceBoardOpen / evidence).
 */
export default function EvidenceBoard() {
  const isOpen = useGameStore((s) => s.isEvidenceBoardOpen);
  const evidence = useGameStore((s) => s.evidence);
  const toggleBoard = useGameStore((s) => s.toggleEvidenceBoard);
  const interviewActive = useGameStore((s) => s.interview.active);
  const interferencePhase = useGameStore((s) => s.interferencePhase);
  const shownToHargrove = useGameStore((s) => s.evidenceShown.hargrove);
  const showEvidenceToNpc = useGameStore((s) => s.showEvidenceToNpc);
  const addDialogueLine = useGameStore((s) => s.addDialogueLine);
  const [detailId, setDetailId] = useState(null);
  const [zoomId, setZoomId] = useState(null);

  if (!isOpen) return null;

  const detail = detailId ? getEvidence(detailId) : null;

  // ── Detail view ──
  if (detail) {
    const restricted = !!detail.corruptible;
    const presented = (shownToHargrove || []).includes(detail.id);
    const connections = (detail.connections || [])
      .map((cid) => getEvidence(cid)?.title)
      .filter(Boolean);
    const present = () => {
      if (presented) return;
      showEvidenceToNpc('hargrove', detail.id);
      addDialogueLine({
        text: `[Craine slides it across the desk.] ◆ EVIDENCE PRESENTED: ${detail.title}`,
        type: 'evidence',
        instant: true,
      });
    };
    const lbl = { color: 'var(--text-muted)', letterSpacing: 'var(--tracking-label)' };
    return (
      <div className="glass-wide am">
        <EvidenceStyles />
        <div className="glass-t am">◈ {detail.title}<button className="glass-x" onClick={() => setDetailId(null)}>[← BACK]</button></div>
        <EvidenceImage item={detail} className="ds-ev-card-img" onClick={() => setZoomId(detail.id)} />
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', lineHeight: 'var(--leading-body)', margin: '8px 0' }}>{detail.description}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', fontSize: 'var(--text-sm)' }}>
          <span style={lbl}>ORIGIN</span><span style={{ color: 'var(--text-primary)' }}>{humanizeScene(detail.sceneId)}</span>
          <span style={lbl}>CLASS</span><span style={{ color: restricted ? 'var(--amber)' : 'var(--text-primary)' }}>{restricted ? '▓▓ CHANNEL-SENSITIVE ▓▓' : 'UNRESTRICTED'}</span>
          {connections.length > 0 && (<><span style={lbl}>CROSS-REF</span><span style={{ color: 'var(--text-primary)' }}>{connections.join(' · ')}</span></>)}
        </div>
        {interviewActive && (
          <button type="button" className="ds-ev-present" onClick={present} disabled={presented}>
            {presented ? '✓ PRESENTED TO HARGROVE' : '▸ PRESENT TO HARGROVE'}
          </button>
        )}
        {zoomId && <EvidenceZoom item={getEvidence(zoomId)} onClose={() => setZoomId(null)} />}
      </div>
    );
  }

  // ── Grid view ──
  return (
    <div className="glass-wide am">
      <EvidenceStyles />
      <div className="glass-t am">◈ EVIDENCE LOCKER · {evidence.length} ITEM{evidence.length !== 1 ? 'S' : ''}<button className="glass-x" onClick={toggleBoard}>[ESC]</button></div>
      {evidence.length === 0 ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', letterSpacing: 'var(--tracking-label)', marginTop: 20, textAlign: 'center' }}>
          NO EVIDENCE LOGGED YET<br /><span style={{ fontSize: '8px' }}>examine scene objects to collect</span>
        </div>
      ) : (
        <div className="evd-grid">
          {evidence.map((id) => {
            const item = getEvidence(id);
            if (!item) return null;
            const corrupted = item.corruptible && (interferencePhase === 2 || interferencePhase === 3);
            return (
              <div key={id} className="evd-card" onClick={() => setDetailId(id)}>
                <EvidenceImage item={item} className="ds-ev-card-img" />
                <div className="evd-ct">{corrupted ? corruptText(item.title) : item.title}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
